import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ArrowLeft, Plus, Search, Pencil, Trash2, Copy, ExternalLink, ClipboardCheck, Send, Settings,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { logger } from '../../utils/logger';
import { ConsultationClientBackfill } from '../../components/admin/ConsultationClientBackfill';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return iso; }
};

const STATUS_META = {
  draft: { label: 'Draft', color: 'bg-amber-100 text-amber-800 border-amber-200' },
  sent:  { label: 'Sent',  color: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
};

export const ConsultationSummariesList = () => {
  const navigate = useNavigate();
  const password = sessionStorage.getItem('admin_password');
  const auth = useMemo(() => ({ Authorization: `Basic ${btoa(':' + password)}` }), [password]);

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (!password) { navigate('/admin/login'); return; }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/consultation-summaries`, { headers: auth });
      setItems(data.items || []);
    } catch (e) {
      logger.error('list summaries failed', e);
      toast.error('Failed to load summaries');
    } finally {
      setLoading(false);
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter((it) =>
      (it.client_name || '').toLowerCase().includes(q) ||
      (it.client_email || '').toLowerCase().includes(q) ||
      (it.title || '').toLowerCase().includes(q) ||
      (it.destination || '').toLowerCase().includes(q),
    );
  }, [items, search]);

  const duplicate = async (id) => {
    try {
      const { data } = await axios.post(`${API}/admin/consultation-summaries/${id}/duplicate`, {}, { headers: auth });
      toast.success('Duplicated — edit the new draft');
      navigate(`/admin/consultation-summaries/${data.id}/edit`);
    } catch (e) { toast.error('Duplicate failed'); }
  };

  const softDelete = async (id) => {
    if (!window.confirm('Delete this consultation summary? It can be restored later.')) return;
    try {
      await axios.delete(`${API}/admin/consultation-summaries/${id}`, { headers: auth });
      setItems((prev) => prev.filter((x) => x.id !== id));
      toast.success('Summary deleted');
    } catch (e) { toast.error('Delete failed'); }
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="consultation-summaries-list">
      <div className="bg-white border-b border-border sticky top-0 z-10">
        <div className="container mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/admin/dashboard')} className="p-2 rounded-lg hover:bg-gray-100" data-testid="back-to-dashboard"><ArrowLeft className="w-5 h-5" /></button>
            <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center"><ClipboardCheck className="w-5 h-5 text-primary" /></div>
            <div>
              <h1 className="text-base sm:text-xl font-bold text-primary">Consultation Summaries</h1>
              <p className="text-xs text-foreground/60">Post-call recaps · PDF + email</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={() => navigate('/admin/consultation-template')}
              data-testid="edit-template-btn"
              title="Edit the standard Next Steps &amp; Timeline template used by the Prefill button"
            >
              <Settings className="w-4 h-4 mr-1.5" /> Edit template
            </Button>
            <Button onClick={() => navigate('/admin/consultation-summaries/new')} data-testid="new-summary-btn">
              <Plus className="w-4 h-4 mr-1.5" /> New summary
            </Button>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 py-6 space-y-4">
        <ConsultationClientBackfill auth={auth} onDone={load} />
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-foreground/40" />
          <Input
            placeholder="Search by client, title or destination…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
            data-testid="summary-search"
          />
        </div>

        {loading ? (
          <div className="text-center text-foreground/60 py-16">Loading summaries…</div>
        ) : filtered.length === 0 ? (
          <div className="bg-white border border-dashed border-border rounded-xl p-10 text-center">
            <ClipboardCheck className="w-10 h-10 mx-auto text-foreground/30 mb-3" />
            <h3 className="text-base font-semibold text-foreground mb-1">No summaries yet</h3>
            <p className="text-sm text-foreground/60 mb-4">Create your first consultation summary to send an on-brand recap to a client.</p>
            <Button onClick={() => navigate('/admin/consultation-summaries/new')} data-testid="empty-new-btn">
              <Plus className="w-4 h-4 mr-1.5" /> New summary
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filtered.map((it) => {
              const meta = STATUS_META[it.status] || STATUS_META.draft;
              return (
                <div key={it.id} className="bg-white border border-border rounded-xl p-4 hover:border-primary/40 transition-colors group" data-testid={`summary-card-${it.id}`}>
                  <div className="flex items-start justify-between mb-2">
                    <span className={`text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded border ${meta.color}`}>{meta.label}</span>
                    <span className="text-xs text-foreground/50">{fmtDate(it.consultation_date)}</span>
                  </div>
                  <h3 className="text-sm font-semibold text-foreground line-clamp-1">{it.title || 'Consultation Summary'}</h3>
                  <p className="text-xs text-foreground/60 line-clamp-1">{it.client_name || '—'}</p>
                  {it.destination && <p className="text-xs text-foreground/50 mt-1">→ {it.destination}</p>}
                  {it.sent_at && (
                    <p className="text-[11px] text-emerald-700 mt-2">Sent {fmtDate(it.sent_at)} to {it.last_sent_to}</p>
                  )}
                  <div className="flex items-center gap-1.5 mt-3 pt-3 border-t border-border/60">
                    <Button variant="outline" size="sm" onClick={() => navigate(`/admin/consultation-summaries/${it.id}/edit`)} data-testid={`edit-${it.id}`}>
                      <Pencil className="w-3.5 h-3.5 mr-1" /> Edit
                    </Button>
                    <button
                      onClick={async () => {
                        try {
                          const res = await fetch(`${API}/admin/consultation-summaries/${it.id}/pdf`, { headers: auth });
                          if (!res.ok) throw new Error('load failed');
                          const blob = await res.blob();
                          window.open(URL.createObjectURL(blob), '_blank');
                        } catch (e) { toast.error('Could not open PDF'); }
                      }}
                      className="inline-flex items-center gap-1 text-xs px-2 py-1.5 border border-border rounded-md hover:bg-gray-50"
                      data-testid={`pdf-${it.id}`}
                    ><ExternalLink className="w-3.5 h-3.5" /> PDF</button>
                    <button onClick={() => duplicate(it.id)} className="p-1.5 rounded hover:bg-gray-100 text-foreground/60" title="Duplicate" data-testid={`dup-${it.id}`}><Copy className="w-3.5 h-3.5" /></button>
                    <button onClick={() => softDelete(it.id)} className="p-1.5 rounded hover:bg-red-50 text-red-600 ml-auto" title="Delete" data-testid={`del-${it.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default ConsultationSummariesList;
